/**
 * Раздел «Звонки» (этап 4) — вызовы GiperCRM. Адреса и формы ответов сверены с кодом CRM:
 *   журнал, сводка, карточка, запись, расшифровка — GiperCRM/server/src/routes/calls.ts (+ domain/callsList.ts);
 *   оценка по скрипту — routes/callScriptReviews.ts;
 *   итоги звонков «Ждут вашего решения» — routes/callDecisions.ts + services/callDecisions.ts;
 *   решения — routes/callSuggestions.ts (accept / keep), поручения — routes/callTasks.ts.
 */
import config from '@/lib/config';
import { getFreshAccessToken } from '@/lib/authToken';
import { ApiError, fetchWithTimeout, reasonOf } from '@/lib/http';
import { useProjectStore } from '@/store/project';
import { crmFetch } from './crm';

// ── Журнал ──

export type CallOutcome = 'answered' | 'no_answer' | 'busy' | 'failed' | 'live';
/** Вкладка журнала на сервере (domain/callsList.ts → CALL_TABS). */
export type ServerCallTab = 'all' | 'incoming' | 'outgoing' | 'missed' | 'unreached' | 'mine';
/** Область видимости звонков: администратор — all, руководитель по схеме — branch, остальные — own (ADR-011). */
export type CallsScopeKind = 'all' | 'branch' | 'own';

/** Строка журнала (domain/callsList.ts → callView). */
export interface CallItem {
  id: string;
  direction: 'incoming' | 'outgoing';
  /** ringing | in_progress | completed | no_answer | failed */
  status: string;
  outcome: CallOutcome;
  phone: string | null;
  phoneLabel?: string | null;
  durationSeconds: number | null;
  startedAt: string | null;
  occurredAt: string;
  personId: string | null;
  personName: string | null;
  companyName: string | null;
  dealId: string | null;
  dealTitle: string | null;
  pipelineId: string | null;
  pipelineName: string | null;
  stageId: string | null;
  stageName: string | null;
  stageColor: string | null;
  agentUserId: string | null;
  agentName: string | null;
  recordingUrl: string | null;
  hasTranscript: boolean | null;
  summary: string | null;
  pbxUser: string | null;
  /** none | pending | stored | failed */
  recordingState: string | null;
  recordingError: string | null;
  /** none | pending | done | failed | skipped */
  transcriptState: string | null;
  transcriptError: string | null;
  transcriptLang: string | null;
  /** Черновая расшифровка (узбекская/смешанная речь) — сверять с записью. */
  transcriptDraft: boolean | null;
  nextStep: string | null;
  /** null — не пропущенный; объект — перезвонили или ещё нет. */
  callback: { done: boolean; callId: string | null; at: string | null; byName: string | null } | null;
  scriptState: string | null;
  scriptScore: number | null;
}

export interface CallsPage {
  items: CallItem[];
  nextCursor: string | null;
  scope: CallsScopeKind;
  namesUnavailable?: boolean;
  /** Сервер применил отбор mine=1 (CRM с 11.10.2026); старый сервер поля не отдаёт. */
  mine?: boolean;
  /** Страница получена запасным путём (tab=mine, вид отбирается на телефоне) — ставит Saleus, не сервер. */
  legacyMine?: boolean;
}

/** GET /api/crm/calls?… — журнал, свежие сверху, курсор (routes/calls.ts ~71). */
export function getCalls(params: Record<string, string>): Promise<CallsPage> {
  const q = new URLSearchParams(params).toString();
  return crmFetch<CallsPage>(`/api/crm/calls${q ? `?${q}` : ''}`);
}

export interface CallsSummary {
  scope: CallsScopeKind;
  total: number;
  incoming: number;
  outgoing: number;
  answered: number;
  missed: number;
  missedNotCalledBack: number;
  unreached: number;
  totalSeconds: number;
  tabs: Record<ServerCallTab, number>;
  /** Сводка посчитана только по моим звонкам (mine=1 понят сервером). */
  mine?: boolean;
}

/** GET /api/crm/calls/summary?from&to[&mine=1] — сводка за период (без вкладки, routes/calls.ts ~129). */
export function getCallsSummary(params: Record<string, string>): Promise<CallsSummary> {
  const q = new URLSearchParams(params).toString();
  return crmFetch<CallsSummary>(`/api/crm/calls/summary${q ? `?${q}` : ''}`);
}

export interface CallCard extends CallItem {
  canEdit?: boolean;
}

/** GET /api/crm/calls/:id — карточка звонка (routes/calls.ts ~325). Чужой звонок — 404. */
export const getCall = (id: string) => crmFetch<CallCard>(`/api/crm/calls/${encodeURIComponent(id)}`);

export interface TranscriptSegment {
  startMs: number;
  endMs: number;
  speaker: string;
  role: 'operator' | 'client' | null;
  text: string;
}

export interface CallTranscript {
  state: string;
  error: string | null;
  language: string | null;
  draft: boolean;
  summary: string | null;
  nextStep: string | null;
  segments: TranscriptSegment[];
}

/** GET /api/crm/calls/:id/transcript — реплики с временем (routes/calls.ts ~454). */
export const getTranscript = (id: string) => crmFetch<CallTranscript>(`/api/crm/calls/${encodeURIComponent(id)}/transcript`);

export type ReviewStatus = 'done' | 'missed' | 'na' | 'handled' | 'unhandled' | 'not_raised' | 'violated' | 'clean' | 'unclear';

/** Разбор звонка по скрипту этапа (routes/callScriptReviews.ts ~130). */
export interface ScriptReview {
  state: 'none' | 'pending' | 'done' | 'no_script' | 'failed';
  reason?: string | null;
  stageName?: string | null;
  score?: number | null;
  summary?: string | null;
  items?: Array<{ id: string; kind: string; text: string; required: boolean; status: ReviewStatus; quote: string | null; atMs: number | null }>;
  manual?: { score: number | null } | null;
}

export const getScriptReview = (id: string) => crmFetch<ScriptReview>(`/api/crm/calls/${encodeURIComponent(id)}/script-review`);

/**
 * GET /api/crm/calls/:id/recording — копия записи из закрытого хранилища (routes/calls.ts ~426).
 * У тега audio нет способа передать пропуск, поэтому берём файл с заголовком и играем из памяти.
 * 409 — «Запись ещё копируется» / «Копии записи нет» (текст сервера).
 */
export async function getRecordingBlob(id: string, повтор = true): Promise<Blob> {
  const token = await getFreshAccessToken(!повтор);
  const projectId = useProjectStore.getState().activeProjectId;
  const res = await fetchWithTimeout(`${config.CRM_API_URL}/api/crm/calls/${encodeURIComponent(id)}/recording`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(projectId ? { 'X-Project-Id': projectId } : {}),
    },
  }, 60_000);
  if (res.status === 401 && повтор) return getRecordingBlob(id, false);
  if (!res.ok) throw new ApiError(res.status, await reasonOf(res));
  return res.blob();
}

// ── Итоги звонков (CC-24) ──

export type DecisionKind = 'proposal' | 'manual' | 'tasks';

export interface CallSuggestion {
  state: string;
  stageId: string | null;
  stageName: string | null;
  nextStepTitle: string | null;
  nextStepAt: string | null;
  lostReason: string | null;
  confidence: number | null;
  decision: string | null;
  gateOk: boolean | null;
  gateMissing: Array<{ message: string }>;
}

export interface CallTaskProposal {
  id: string;
  title: string;
  assignee: { memberId: string; userId: string | null; name: string | null } | null;
  decision: string | null;
}

export interface DecisionItem {
  callId: string;
  kind: DecisionKind;
  why: string | null;
  deal: { id: string; title: string | null; stageId: string | null; stageName: string | null; pipelineId: string | null; ownerId: string | null };
  person: { id: string; name: string | null } | null;
  phone: string | null;
  direction: 'incoming' | 'outgoing';
  endedAt: string;
  durationSeconds: number | null;
  summary: string | null;
  nextStep: string | null;
  ageMinutes: number;
  old: boolean;
  suggestion: CallSuggestion | null;
  tasks: { proposals: CallTaskProposal[] } | null;
}

export interface QuickTime { key: string; label: string; at: string }
export interface DecisionStage { id: string; name: string; stageType: string; position: number }

export interface DecisionList {
  items: DecisionItem[];
  total: number;
  old: number;
  blocked: boolean;
  quickTimes: QuickTime[];
  stages: Record<string, DecisionStage[]>;
}

/** GET /api/crm/call-decisions — мои итоги, ждущие решения (routes/callDecisions.ts ~23). */
export const getDecisions = () => crmFetch<DecisionList>('/api/crm/call-decisions');

export interface DecisionAnswer { ok: boolean; decision: string; moved?: boolean; taskId?: string | null; taskProblem?: string | null }

/** Решение по итогу: путь и тело собирает lib/callDecisions.ts → decisionRequest. */
export const sendDecision = (path: string, body: Record<string, unknown>) =>
  crmFetch<DecisionAnswer>(path, { method: 'POST', body: JSON.stringify(body) });

/** POST /api/crm/calls/:id/task-proposals/:pid/approve — поручение из разговора как предложено (routes/callTasks.ts). */
export const approveCallTask = (callId: string, proposalId: string) =>
  crmFetch<unknown>(`/api/crm/calls/${encodeURIComponent(callId)}/task-proposals/${encodeURIComponent(proposalId)}/approve`, {
    method: 'POST', body: JSON.stringify({}),
  });

// ── Сделка для «Открыть сделку» ──

export interface DealBrief {
  id: string;
  title: string;
  chatwootAccountId: number | null;
  chatwootDisplayId: number | null;
  person?: { id: string; firstName?: string | null; lastName?: string | null; phones?: string[] | null } | null;
}

/** GET /api/crm/deals/:id — нам из неё нужна только привязанная переписка (routes/deals.ts ~168). */
export const getDealBrief = (id: string) => crmFetch<DealBrief>(`/api/crm/deals/${encodeURIComponent(id)}`);
